import { useFormik, FormikErrors, FormikTouched } from 'formik'
import { Alpha2Code, alpha3ToAlpha2 } from 'i18n-iso-countries'
import { FocusEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useLocation, useNavigate } from 'react-router-dom'

import { onboarding } from '@percent/workplace-giving/api/onboarding/onboarding'
import { useMutation } from '@percent/workplace-giving/common/hooks/useMutation/useMutation'
import { useAuth, useQuery } from '@percent/workplace-giving/common/hooks'
import {
  getAccountConfigFromAuthState,
  getAccountFromAuthState,
  getCountryCodeFromAuthState,
  getGivingProfileFromAuthState,
  getTimeZoneFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { getCurrencies } from '@percent/workplace-giving/api/currency/currencies'
import { getPartnerCurrencies } from '@percent/workplace-giving/api/currency/supported-currencies/supported-currencies'
import { useSharedValidationRules } from '@percent/workplace-giving/common/hooks/useSharedValidationRules/useSharedValidationRules'
import { FlagAvatar } from '@percent/lemonade'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'

type ProfileCreationFields = Readonly<{
  fullName: string
  preferredName: string
  preferredCurrency?: string
  termsAndConditions: boolean
  secondaryEmail?: string
}>

type CurrencyOptionProps = Readonly<{
  prefix: JSX.Element
  label: string
  value: string
  flag: Alpha2Code
}>

export type UseProfileCreationReturn = Readonly<{
  isLoading: boolean
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void
  touched: FormikTouched<ProfileCreationFields>
  errors: FormikErrors<ProfileCreationFields>
  error: string
  handleChange: (e: FormEvent<HTMLInputElement>) => void
  isSubmitting: boolean
  buttonDisabled: boolean
  handleBlur: (e: FocusEvent<HTMLInputElement>) => void
  values: ProfileCreationFields
  currencyOptions?: CurrencyOptionProps[]
  setFieldValue: (
    field: string,
    value: string,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<ProfileCreationFields>>
  defaultCurrencyOption?: CurrencyOptionProps
  showCurrencyOptions?: boolean
}>

export const useProfileCreation = (): UseProfileCreationReturn => {
  const { t } = useTranslation()
  const { state, dispatch } = useAuth()
  const { track } = useAnalytics()
  const user = getAccountFromAuthState(state)
  const userCountry = getCountryCodeFromAuthState(state)
  const accountConfig = getAccountConfigFromAuthState(state)
  const givingProfile = getGivingProfileFromAuthState(state)
  const timeZone = getTimeZoneFromAuthState(state)
  const { validateString, validateEmail } = useSharedValidationRules()
  const navigate = useNavigate()
  const location = useLocation().state as { from?: Location } | null
  const [error, setError] = useState('')

  const { data: currencyInfo, isPending: currencyInfoLoading } = useQuery(['getCurrencies'], getCurrencies, {
    enabled: !accountConfig?.currency
  })

  const { data: partnerCurrencyInfo, isPending: partnerCurrencyInfoLoading } = useQuery(
    ['getPartnerCurrencies'],
    getPartnerCurrencies,
    {
      enabled: !accountConfig?.currency
    }
  )

  const currencyOptions = useMemo(
    () =>
      currencyInfo
        ?.filter(
          currency =>
            !!currency.supportedByStripe && (!partnerCurrencyInfo || partnerCurrencyInfo.includes(currency.code))
        )
        .map(currency => ({
          prefix: <FlagAvatar code={currency.flagCode} />,
          label: `${currency.name} (${currency.code})`,
          value: currency.code,
          flag: currency.flagCode
        })),
    [currencyInfo, partnerCurrencyInfo]
  )

  const defaultCurrencyOption = useMemo(() => {
    if (currencyOptions?.length === 1) {
      return currencyOptions[0]
    }

    if (accountConfig?.currency) {
      return currencyOptions?.find(option => option.value === accountConfig.currency)
    }

    if (userCountry) {
      return currencyOptions?.find(option => option.flag === alpha3ToAlpha2(userCountry))
    }

    if (currencyOptions && currencyOptions.length > 0) {
      return currencyOptions[0]
    }

    return undefined
  }, [accountConfig?.currency, currencyOptions, userCountry])

  const { mutateAsync } = useMutation({
    mutationFn: onboarding,
    onSuccess: res => {
      if (state.status === 'authorized') {
        try {
          dispatch({
            type: 'UPDATE',
            payload: {
              account: res.account,
              partner: state.partner,
              acceptedTerms: res.terms,
              accConfig: res.accConfig
            }
          })

          const targetSearchParams = new URLSearchParams(location?.from?.search)

          if (location?.from?.pathname) targetSearchParams.append('redirect_to', location?.from?.pathname)

          navigate(`${AppRoute.PLATFORM_TOUR}${targetSearchParams.size ? `?${targetSearchParams.toString()}` : ''}`, {
            replace: true,
            state: location
          })
        } catch {
          setError(t('workplace_giving.errors.unexpected'))
        }
      }
    },
    onError: () => {
      setError(t('workplace_giving.errors.unexpected'))
    }
  })

  const showCurrencyOptions =
    currencyOptions !== undefined && (accountConfig?.currency === undefined || accountConfig?.currency === null)

  const {
    dirty,
    isValid,
    errors,
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    isSubmitting,
    setFieldValue
  } = useFormik({
    initialValues: {
      showCurrencyOptions,
      fullName: user?.fullName || '',
      preferredName: user?.preferredName || '',
      preferredCurrency: defaultCurrencyOption?.value,
      termsAndConditions: false,
      secondaryEmail: givingProfile?.secondaryEmail || undefined
    },
    validationSchema: () =>
      Yup.object().shape({
        showCurrencyOptions: Yup.boolean(),
        fullName: validateString(),
        preferredName: validateString(),
        secondaryEmail: validateEmail(true)
          .trim()
          .test(
            'secondaryEmailDifferentThanWorkEmail',
            t('workplace_giving.validation.secondaryEmailSameAsWorkEmail'),
            (value: string | null | undefined) => {
              return value !== user?.email
            }
          ),
        preferredCurrency: validateString({ optional: !values.showCurrencyOptions }),
        termsAndConditions: Yup.boolean().oneOf([true], t('workplace_giving.validation.requiredField'))
      }),
    onSubmit: ({ fullName, preferredName, preferredCurrency, secondaryEmail }) => {
      setError('')
      document.body.setAttribute('style', 'overflow-y:auto')

      track({ event: 'Onboarding Setup Continue Button Clicked' })

      return mutateAsync({
        fullName: fullName.trim(),
        preferredName: preferredName.trim(),
        secondaryEmail: secondaryEmail?.trim(),
        currency: preferredCurrency,
        terms: ['percent:privacy:2025-12-15', 'workplace:terms:2025-12-15'],
        timeZone
      })
    }
  })
  const buttonDisabled = !dirty || !isValid || isSubmitting

  // this updates the default currency and ensures the currency is validated if required
  useEffect(() => {
    setFieldValue('preferredCurrency', defaultCurrencyOption?.value)
    setFieldValue('showCurrencyOptions', showCurrencyOptions)
  }, [accountConfig?.currency, defaultCurrencyOption, showCurrencyOptions, setFieldValue])

  useEffect(() => {
    if (values.termsAndConditions) {
      track({ event: 'Onboarding Terms and Conditions Accepted' })
    }
  }, [values.termsAndConditions, track])

  return {
    isLoading: !accountConfig?.currency && (currencyInfoLoading || partnerCurrencyInfoLoading),
    handleSubmit,
    touched,
    errors,
    error,
    handleChange,
    isSubmitting,
    buttonDisabled,
    handleBlur,
    values,
    currencyOptions,
    setFieldValue,
    defaultCurrencyOption,
    showCurrencyOptions
  }
}
